<template>
  <ModalFormularioV2
    v-model="aberto"
    :titulo="isEdicao ? 'Editar Credencial de Emissão' : 'Nova Credencial de Emissão'"
    :loading="isLoading"
    tamanho="lg"
    cor-header="var(--bs-primary)"
    @confirmar="salvar"
  >
    <div class="alert alert-info border-0 shadow-sm small py-2 mb-4 d-flex align-items-start">
      <i class="bi bi-info-circle-fill me-2 mt-1"></i>
      <div>
        Credencial do provedor de emissão fiscal (ex.: Notaas) desta filial.
        <strong>A API Key é criptografada no servidor e nunca é exibida.</strong>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-md-6">
        <CampoSelect
          id="lstProvedor"
          label="Provedor"
          v-model="form.lstProvedor"
          :options="listas.LstProvedor || []"
          option-value="idListaClienteItem"
          option-label="textoLista"
          :disabled="isLoading || isEdicao"
          required
        />
      </div>
      <div class="col-md-6">
        <CampoSelect
          id="lstAmbiente"
          label="Ambiente"
          v-model="form.lstAmbiente"
          :options="listas.LstAmbienteNfe || []"
          option-value="idListaClienteItem"
          option-label="textoLista"
          :disabled="isLoading"
          required
        />
      </div>
      <div class="col-md-6">
        <CampoInput
          id="provedorProjectId"
          label="ID do Projeto no Provedor"
          v-model="form.provedorProjectId"
          placeholder="Ex.: UUID do projeto Notaas"
          :disabled="isLoading"
        />
      </div>
      <div class="col-md-6">
        <CampoInput
          id="apiKey"
          label="API Key"
          type="password"
          v-model="form.apiKey"
          :placeholder="isEdicao ? 'Deixe em branco para manter a atual' : 'Cole a API Key do projeto'"
          :required="!isEdicao"
          :disabled="isLoading"
        />
      </div>
      <div class="col-md-6">
        <CampoSelect
          id="isAtivo"
          label="Ativo"
          v-model="form.isAtivo"
          :options="opcoesSimNao"
          option-value="idListaClienteItem"
          option-label="textoLista"
          :disabled="isLoading"
        />
      </div>
    </div>
  </ModalFormularioV2>
</template>

<script setup>
import { ref, computed, reactive, watch, onMounted } from 'vue'
import api from '@/services/api'
import { useMetadataListasStore } from '@/stores/metadataListas'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  idFilial: { type: Number, default: 0 },
  credencial: { type: Object, default: null }
})

const emit = defineEmits(['update:modelValue', 'salvo', 'show-notification'])

const metadataStore = useMetadataListasStore()

const aberto = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const isLoading = ref(false)
const listas = reactive({})
const opcoesSimNao = [
  { idListaClienteItem: true, textoLista: 'Sim' },
  { idListaClienteItem: false, textoLista: 'Não' }
]

const form = reactive({
  lstProvedor: '',
  provedorProjectId: '',
  apiKey: '',
  lstAmbiente: '',
  isAtivo: true,
  xmin: 0
})

const isEdicao = computed(() => !!(props.credencial && props.credencial.idFilialProvedorFiscal))

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['FilialProvedorFiscal', 'DocumentoSaidaFiscal'])
    Object.assign(listas, result)
  } catch (e) {
    console.error('[FilialProvedorFiscalModal] erro ao carregar listas:', e)
  }
}

const resetForm = () => {
  if (isEdicao.value) {
    const c = props.credencial
    form.lstProvedor = c.lstProvedor || ''
    form.provedorProjectId = c.provedorProjectId || ''
    form.apiKey = ''
    form.lstAmbiente = c.lstAmbiente || ''
    form.isAtivo = c.isAtivo ?? true
    form.xmin = c.xmin || 0
  } else {
    form.lstProvedor = ''
    form.provedorProjectId = ''
    form.apiKey = ''
    form.lstAmbiente = ''
    form.isAtivo = true
    form.xmin = 0
  }
}

watch(() => props.modelValue, (val) => {
  if (val) resetForm()
})

const salvar = async () => {
  if (!form.lstProvedor) {
    emit('show-notification', { message: 'Selecione o Provedor.', type: 'warning' })
    return
  }
  if (!form.lstAmbiente) {
    emit('show-notification', { message: 'Selecione o Ambiente.', type: 'warning' })
    return
  }
  if (!isEdicao.value && !form.apiKey) {
    emit('show-notification', { message: 'A API Key é obrigatória.', type: 'warning' })
    return
  }

  isLoading.value = true

  const payload = {
    idFilial: props.idFilial,
    lstProvedor: form.lstProvedor,
    provedorProjectId: form.provedorProjectId || null,
    apiKey: form.apiKey || null,
    lstAmbiente: form.lstAmbiente,
    isAtivo: form.isAtivo,
    xmin: form.xmin
  }

  try {
    if (isEdicao.value) {
      await api.put(`/FilialProvedoresFiscais/${props.credencial.idFilialProvedorFiscal}`, payload)
      emit('show-notification', { message: 'Credencial atualizada com sucesso.', type: 'success' })
    } else {
      await api.post('/FilialProvedoresFiscais', payload)
      emit('show-notification', { message: 'Credencial cadastrada com sucesso.', type: 'success' })
    }
    emit('salvo')
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar credencial.'

    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }

    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

onMounted(carregarListas)
</script>
